import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search, Upload, CheckCircle } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { useQuery } from "@tanstack/react-query";

const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";

const INSURANCE_OPTIONS = [
  { value: 'insured', label: 'Private Insurance' },
  { value: 'uninsured', label: 'Uninsured' },
  { value: 'underinsured', label: 'Underinsured' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'other', label: 'Other' },
];

export function StorySubmissionForm({ onSuccess }: { onSuccess?: () => void }) {
  const { toast } = useToast();
  const [title, setTitle] = useState('');
  const [storyText, setStoryText] = useState('');
  const [amountCharged, setAmountCharged] = useState('');
  const [amountExpected, setAmountExpected] = useState('');
  const [insuranceStatus, setInsuranceStatus] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorZip, setAuthorZip] = useState('');
  const [hospitalSearch, setHospitalSearch] = useState('');
  const [selectedHospital, setSelectedHospital] = useState<{ id: string; name: string; city: string; state: string } | null>(null);
  const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const honeypotRef = useRef<HTMLInputElement>(null);
  const formStartRef = useRef(Date.now());

  const debouncedSearch = useDebounce(hospitalSearch, 300);

  const { data: hospitalResults = [] } = useQuery({
    queryKey: ['hospital-search', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('hospitals')
        .select('id, name, city, state')
        .ilike('name', `%${debouncedSearch}%`)
        .limit(8);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (honeypotRef.current?.value) return;

    // Time-based bot check (must take > 5 seconds)
    if (Date.now() - formStartRef.current < 5000) return;

    if (!title.trim() || !storyText.trim()) {
      toast({ title: "Please fill in the required fields", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from('community_stories').insert({
        hospital_id: selectedHospital?.id || null,
        title: title.trim(),
        story_text: storyText.trim(),
        amount_charged: amountCharged ? parseFloat(amountCharged) : null,
        amount_expected: amountExpected ? parseFloat(amountExpected) : null,
        insurance_status: insuranceStatus || null,
        author_name: authorName.trim() || 'Anonymous',
        author_zip: authorZip.trim() || null,
        moderation_status: 'pending',
      });

      if (error) throw error;

      setSubmitted(true);
      toast({ title: "Your story has been submitted for review" });
      onSuccess?.();
    } catch (err: any) {
      toast({ title: "Failed to submit story", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12 px-6">
        <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#2b5e49' }} />
        <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: serif, color: '#1B1B1B' }}>
          Thank You for Sharing
        </h3>
        <p className="text-sm max-w-md mx-auto" style={{ color: 'rgba(27,27,27,0.60)', fontFamily: sans }}>
          Your story will appear on the community feed after review. Together, we're bringing transparency to American healthcare pricing.
        </p>
      </div>
    );
  }

  const inputStyle = {
    fontFamily: sans,
    background: '#FFFFFF',
    border: '1px solid rgba(27,27,27,0.18)',
    color: '#1B1B1B',
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" style={{ fontFamily: sans }}>
      {/* Honeypot — hidden from real users */}
      <input
        ref={honeypotRef}
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
      />

      {/* Hospital autocomplete */}
      <div className="relative">
        <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1B1B1B' }}>
          Hospital <span style={{ color: 'rgba(27,27,27,0.35)', fontWeight: 400 }}>(optional)</span>
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(27,27,27,0.30)' }} />
          <input
            type="text"
            value={selectedHospital ? selectedHospital.name : hospitalSearch}
            onChange={(e) => {
              setHospitalSearch(e.target.value);
              setSelectedHospital(null);
              setShowHospitalDropdown(true);
            }}
            onFocus={() => setShowHospitalDropdown(true)}
            onBlur={() => setTimeout(() => setShowHospitalDropdown(false), 200)}
            placeholder="Search for a hospital..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg text-sm"
            style={inputStyle}
          />
        </div>
        {showHospitalDropdown && hospitalResults.length > 0 && !selectedHospital && (
          <div
            className="absolute z-20 w-full mt-1 rounded-lg shadow-lg overflow-hidden"
            style={{ background: '#FFFFFF', border: '1px solid rgba(27,27,27,0.12)' }}
          >
            {hospitalResults.map((h: any) => (
              <button
                key={h.id}
                type="button"
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAF9F6] transition-colors"
                onClick={() => {
                  setSelectedHospital(h);
                  setHospitalSearch('');
                  setShowHospitalDropdown(false);
                }}
              >
                <span className="font-medium">{h.name}</span>
                <span className="ml-2 text-xs" style={{ color: 'rgba(27,27,27,0.45)' }}>
                  {h.city}, {h.state}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1B1B1B' }}>
          Story Title <span style={{ color: '#B22234' }}>*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Charged $8,000 for a 10-minute ER visit"
          className="w-full px-4 py-2.5 rounded-lg text-sm"
          style={inputStyle}
          required
        />
      </div>

      {/* Story text */}
      <div>
        <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1B1B1B' }}>
          What Happened? <span style={{ color: '#B22234' }}>*</span>
        </label>
        <textarea
          value={storyText}
          onChange={(e) => setStoryText(e.target.value)}
          placeholder="Tell us your experience with hospital pricing..."
          rows={5}
          className="w-full px-4 py-2.5 rounded-lg text-sm resize-y"
          style={inputStyle}
          required
        />
      </div>

      {/* Pricing row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1B1B1B' }}>
            Amount Charged
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'rgba(27,27,27,0.40)' }}>$</span>
            <input
              type="number"
              value={amountCharged}
              onChange={(e) => setAmountCharged(e.target.value)}
              placeholder="0"
              className="w-full pl-7 pr-4 py-2.5 rounded-lg text-sm"
              style={inputStyle}
              min="0"
              step="0.01"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1B1B1B' }}>
            Amount Expected
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'rgba(27,27,27,0.40)' }}>$</span>
            <input
              type="number"
              value={amountExpected}
              onChange={(e) => setAmountExpected(e.target.value)}
              placeholder="0"
              className="w-full pl-7 pr-4 py-2.5 rounded-lg text-sm"
              style={inputStyle}
              min="0"
              step="0.01"
            />
          </div>
        </div>
      </div>

      {/* Insurance + Name row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1B1B1B' }}>
            Insurance Status
          </label>
          <select
            value={insuranceStatus}
            onChange={(e) => setInsuranceStatus(e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg text-sm"
            style={inputStyle}
          >
            <option value="">Select...</option>
            {INSURANCE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1B1B1B' }}>
            Your Name
          </label>
          <input
            type="text"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            placeholder="Anonymous"
            className="w-full px-4 py-2.5 rounded-lg text-sm"
            style={inputStyle}
          />
        </div>
      </div>

      {/* ZIP */}
      <div className="max-w-[200px]">
        <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1B1B1B' }}>
          ZIP Code <span style={{ color: 'rgba(27,27,27,0.35)', fontWeight: 400 }}>(optional)</span>
        </label>
        <input
          type="text"
          value={authorZip}
          onChange={(e) => setAuthorZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
          placeholder="12345"
          className="w-full px-4 py-2.5 rounded-lg text-sm"
          style={inputStyle}
          maxLength={5}
          pattern="\d{5}"
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3 rounded-lg text-white font-semibold text-sm transition-opacity disabled:opacity-60"
        style={{ background: '#B22234', fontFamily: sans }}
      >
        {submitting ? 'Submitting...' : 'Share Your Story'}
      </button>

      <p className="text-xs text-center" style={{ color: 'rgba(27,27,27,0.40)' }}>
        Your story will appear after review. We never share your email.
      </p>
    </form>
  );
}
